<template>
    <div class="listener-favourites" @scroll.passive="listenBottomOut" ref="scroller">
        <section class="listener-favourites__hero">
            <div class="listener-favourites__header">
                <div>
                    <div class="listener-favourites__eyebrow">{{ $tt('喜爱') }}</div>
                    <h1 class="listener-favourites__title">{{ pageTitle }}</h1>
                </div>
                <div class="listener-favourites__badge">{{ activeItems.length }}</div>
            </div>

            <div class="listener-favourites__tabs">
                <button
                    v-for="{ value, text } in options"
                    :key="value"
                    :class="['listener-favourites__tab', sortValue === value ? 'is-active' : '']"
                    @click="switchSort(value)"
                >
                    {{ text }}
                </button>
            </div>

            <div class="listener-favourites__summary">
                <div class="listener-favourites__summary-kicker">{{ $tt('我的收藏') }}</div>
                <div class="listener-favourites__summary-title">
                    {{ $tt('已收录 {count} 个{label}', { count: activeItems.length, label: activeLabel }) }}
                </div>
                <div class="listener-favourites__summary-sub">
                    {{ summaryText }}
                </div>
                <div v-if="sortValue === 'tracks' && activeItems.length" class="listener-favourites__actions">
                    <button class="listener-favourites__action listener-favourites__action--primary" @click="playTracks">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                        </svg>
                        <span>{{ $tt('播放全部') }}</span>
                    </button>
                    <button class="listener-favourites__action" @click="shuffleTracks">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17 3h4v4h-2V6.41l-3.29 3.3-1.42-1.42L17.59 5H17V3zM3 7h5l3.29 3.29-1.42 1.42L7.17 9H3V7zm18 10v4h-4v-2h.59l-3.3-3.29 1.42-1.42 3.29 3.3V17H21zM3 15h4.17l10-10H21V3h-4v2h.59L7.17 15H3v2z"></path>
                        </svg>
                        <span>{{ $tt('打乱') }}</span>
                    </button>
                </div>
            </div>
        </section>

        <section v-if="sortValue === 'albums' && activeItems.length" class="listener-favourites__section">
            <div class="listener-favourites__grid">
                <div
                    v-for="album in details.albums"
                    :key="album.id"
                    class="listener-favourites__album-card"
                >
                    <button class="listener-favourites__album-main" @click="redirectAlbum(album)">
                        <img
                            :src="album.image || '/static/dist/img/album_null.jpg'"
                            :alt="album.name"
                            class="listener-favourites__album-image"
                        >
                        <div class="listener-favourites__album-title">{{ album.name }}</div>
                        <div class="listener-favourites__album-sub">{{ album.artist || $tt('未知艺术家') }}</div>
                    </button>
                    <button
                        class="listener-favourites__fav-button"
                        @click.stop="toggleFavourite(album, 'album')"
                    >
                        <svg viewBox="0 0 24 24" :fill="album.favourite ? '#ef4444' : 'none'" stroke="currentColor" stroke-width="2">
                            <path d="M12 21s-6.72-4.35-9.2-8.3A5.38 5.38 0 0 1 7.35 4.6 5.2 5.2 0 0 1 12 7.09 5.2 5.2 0 0 1 16.65 4.6a5.38 5.38 0 0 1 4.55 8.1C18.72 16.65 12 21 12 21z"></path>
                        </svg>
                    </button>
                </div>
            </div>
        </section>

        <section v-else-if="sortValue === 'artists' && activeItems.length" class="listener-favourites__section">
            <div class="listener-favourites__list">
                <button
                    v-for="artist in details.artists"
                    :key="artist.id"
                    class="listener-favourites__row"
                    @click="redirectArtist(artist)"
                >
                    <img
                        :src="artist.image || '/static/dist/img/album_null.jpg'"
                        :alt="artist.name"
                        class="listener-favourites__artist-image"
                    >
                    <div class="listener-favourites__row-meta">
                        <div class="listener-favourites__row-title">{{ artist.name }}</div>
                        <div class="listener-favourites__row-sub">
                            {{ $tt('{count} 张专辑', { count: artist.albumCount || 0 }) }}
                        </div>
                    </div>
                    <button
                        class="listener-favourites__fav-button listener-favourites__fav-button--inline"
                        @click.stop="toggleFavourite(artist, 'artist')"
                    >
                        <svg viewBox="0 0 24 24" :fill="artist.favourite ? '#ef4444' : 'none'" stroke="currentColor" stroke-width="2">
                            <path d="M12 21s-6.72-4.35-9.2-8.3A5.38 5.38 0 0 1 7.35 4.6 5.2 5.2 0 0 1 12 7.09 5.2 5.2 0 0 1 16.65 4.6a5.38 5.38 0 0 1 4.55 8.1C18.72 16.65 12 21 12 21z"></path>
                        </svg>
                    </button>
                    <svg class="listener-favourites__row-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M9 6l6 6-6 6"></path>
                    </svg>
                </button>
            </div>
        </section>

        <section v-else-if="sortValue === 'tracks' && activeItems.length" class="listener-favourites__section">
            <div class="listener-favourites__list">
                <button
                    v-for="(track, index) in details.tracks"
                    :key="track.id || `track-${index}`"
                    :class="['listener-favourites__row', isCurrentTrack(track) ? 'is-current' : '']"
                    @click="playTrack(index)"
                >
                    <img
                        :src="track.image || '/static/dist/img/album_null.jpg'"
                        :alt="track.title"
                        class="listener-favourites__track-image"
                    >
                    <div class="listener-favourites__row-meta">
                        <div class="listener-favourites__row-title">{{ track.title }}</div>
                        <div class="listener-favourites__row-sub">
                            {{ track.artist || $tt('未知艺术家') }}
                            <span v-if="track.album"> · {{ track.album }}</span>
                        </div>
                    </div>
                    <button
                        class="listener-favourites__fav-button listener-favourites__fav-button--inline"
                        @click.stop="toggleFavourite(track, 'track')"
                    >
                        <svg viewBox="0 0 24 24" :fill="track.favourite ? '#ef4444' : 'none'" stroke="currentColor" stroke-width="2">
                            <path d="M12 21s-6.72-4.35-9.2-8.3A5.38 5.38 0 0 1 7.35 4.6 5.2 5.2 0 0 1 12 7.09 5.2 5.2 0 0 1 16.65 4.6a5.38 5.38 0 0 1 4.55 8.1C18.72 16.65 12 21 12 21z"></path>
                        </svg>
                    </button>
                    <div class="listener-favourites__duration">{{ formatDuration(track.duration) }}</div>
                </button>
            </div>
        </section>

        <section v-else-if="!loading" class="listener-favourites__empty">
            <div class="listener-favourites__empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                    <path d="M12 21s-6.72-4.35-9.2-8.3A5.38 5.38 0 0 1 7.35 4.6 5.2 5.2 0 0 1 12 7.09 5.2 5.2 0 0 1 16.65 4.6a5.38 5.38 0 0 1 4.55 8.1C18.72 16.65 12 21 12 21z"></path>
                </svg>
            </div>
            <h2>{{ $tt('这里还没有内容') }}</h2>
            <p>{{ emptyText }}</p>
        </section>

        <section v-if="loading" class="listener-favourites__state">
            <div class="listener-favourites__loading"></div>
            <p>{{ $tt('正在加载...') }}</p>
        </section>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    name: 'ListenerFavourites',
    data() {
        return {
            options: [
                { text: this.$tt('专辑'), value: 'albums' },
                { text: this.$tt('艺术家'), value: 'artists' },
                { text: this.$tt('歌曲'), value: 'tracks' }
            ],
            loading: false,
            details: { albums: [], artists: [], tracks: [] },
            offset: 0,
            hasMore: true
        }
    },
    computed: {
        ...mapGetters(['track']),
        sortValue() {
            return this.$route.params.sort || 'albums'
        },
        activeItems() {
            return this.details[this.sortValue] || []
        },
        activeLabel() {
            const labelMap = {
                albums: this.$tt('专辑'),
                artists: this.$tt('艺术家'),
                tracks: this.$tt('歌曲')
            }
            return labelMap[this.sortValue] || this.$tt('内容')
        },
        pageTitle() {
            const titleMap = {
                albums: this.$tt('喜爱的专辑'),
                artists: this.$tt('喜爱的艺术家'),
                tracks: this.$tt('喜爱的歌曲')
            }
            return titleMap[this.sortValue] || this.$tt('我的喜爱')
        },
        summaryText() {
            const textMap = {
                albums: this.$tt('把常听专辑集中放在这里，随时继续播放。'),
                artists: this.$tt('收藏过的艺术家会整理在这里，方便继续探索。'),
                tracks: this.$tt('你标记过的歌曲会留在这里，适合直接开播。')
            }
            return textMap[this.sortValue] || ''
        },
        emptyText() {
            const textMap = {
                albums: this.$tt('还没有喜爱的专辑，去发现页收下几张吧。'),
                artists: this.$tt('还没有喜爱的艺术家，先去挑几个喜欢的声音。'),
                tracks: this.$tt('还没有喜爱的歌曲，点亮心形后会出现在这里。')
            }
            return textMap[this.sortValue] || ''
        }
    },
    created() {
        this.loadMore()
    },
    watch: {
        sortValue() {
            this.offset = this.activeItems.length
            this.hasMore = true
            if (this.activeItems.length === 0) {
                this.loadMore()
                return
            }
            this.$nextTick(() => {
                this.listenBottomOut()
            })
        }
    },
    methods: {
        switchSort(value) {
            if (value === this.sortValue) {
                return
            }
            this.$router.push({ path: '/favourites/' + value })
        },
        loadMore() {
            if (this.loading || !this.hasMore) {
                return
            }
            this.loading = true
            return this.$api.subsonic.getFavourites(this.sortValue, 30, this.offset).then(details => {
                const items = details[this.sortValue] || []
                this.details[this.sortValue].push(...items)
                this.offset += items.length
                this.hasMore = items.length > 0
                this.loading = false
            }).catch(() => {
                this.loading = false
            })
        },
        listenBottomOut() {
            if (!this.hasMore || this.loading || !this.$refs.scroller) {
                return
            }
            const scroller = this.$refs.scroller
            if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 160) {
                this.loadMore()
            }
        },
        redirectArtist(item) {
            if (!item || !item.id) {
                return
            }
            this.$router.push({ path: '/artist/id/' + item.id })
        },
        redirectAlbum(item) {
            if (!item || !item.id) {
                return
            }
            this.$router.push({ path: '/album/id/' + item.id })
        },
        playTrack(index) {
            this.$store.dispatch('playTrackList', {
                index,
                tracks: this.details.tracks
            })
        },
        playTracks() {
            this.$store.dispatch('playTrackList', {
                tracks: this.details.tracks
            })
        },
        shuffleTracks() {
            if (!this.details.tracks.length) {
                return
            }
            const index = Math.floor(Math.random() * this.details.tracks.length)
            this.playTrack(index)
        },
        isCurrentTrack(item) {
            return !!(this.track && item && this.track.id === item.id)
        },
        toggleFavourite(item, type) {
            const action = item.favourite ? 'removeFavourite' : 'addFavourite'
            this.$api.subsonic[action](item.id, type).then(() => {
                item.favourite = !item.favourite
                if (!item.favourite) {
                    const list = this.details[this.sortValue]
                    const index = list.findIndex(entry => entry.id === item.id)
                    if (index !== -1) {
                        list.splice(index, 1)
                        this.offset = list.length
                    }
                }
                if (type === 'track' && this.track && this.track.id === item.id) {
                    this.track.favourite = item.favourite
                }
                this.$nextTick(() => {
                    this.listenBottomOut()
                })
            })
        },
        formatDuration(value) {
            if (!isFinite(value)) {
                return '∞'
            }
            const minutes = Math.floor(value / 60)
            const seconds = Math.floor(value % 60)
            return (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds
        }
    }
}
</script>

<style scoped>
.listener-favourites {
    height: calc(100dvh - 12.25rem);
    padding: 0.45rem 0.1rem 0.25rem;
    box-sizing: border-box;
    overflow-y: auto;
}

.listener-favourites__hero {
    padding: 0.3rem 0.25rem 0.2rem;
}

.listener-favourites__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}

.listener-favourites__eyebrow {
    font-size: 0.85rem;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.04em;
}

.listener-favourites__title {
    margin-top: 0.2rem;
    font-size: 2rem;
    line-height: 1;
    font-weight: 800;
    color: #111827;
    letter-spacing: -0.04em;
}

.listener-favourites__badge {
    min-width: 2.8rem;
    height: 2.8rem;
    padding: 0 0.8rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(17, 24, 39, 0.95);
    color: #fff;
    font-size: 0.95rem;
    font-weight: 700;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.18);
}

.listener-favourites__tabs {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.55rem;
}

.listener-favourites__tab {
    border: 0;
    border-radius: 999px;
    padding: 0.72rem 0.4rem;
    background: rgba(255, 255, 255, 0.85);
    color: #64748b;
    font-size: 0.84rem;
    font-weight: 700;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.1);
}

.listener-favourites__tab.is-active {
    background: #111827;
    color: #fff;
    box-shadow: 0 16px 30px rgba(15, 23, 42, 0.2);
}

.listener-favourites__summary {
    margin-top: 0.95rem;
    padding: 1rem;
    border-radius: 1.65rem;
    background: linear-gradient(135deg, rgba(17, 24, 39, 0.96), rgba(55, 65, 81, 0.92));
    color: #fff;
    box-shadow: 0 20px 46px rgba(15, 23, 42, 0.16);
}

.listener-favourites__summary-kicker {
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.64);
}

.listener-favourites__summary-title {
    margin-top: 0.35rem;
    font-size: 1.08rem;
    font-weight: 700;
    line-height: 1.4;
}

.listener-favourites__summary-sub {
    margin-top: 0.32rem;
    font-size: 0.8rem;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.68);
}

.listener-favourites__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.65rem;
    margin-top: 0.9rem;
}

.listener-favourites__action {
    border: 0;
    border-radius: 1rem;
    padding: 0.78rem 0.65rem;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    font-size: 0.82rem;
    font-weight: 700;
}

.listener-favourites__action--primary {
    background: #fff;
    color: #111827;
}

.listener-favourites__action svg {
    width: 1.02rem;
    height: 1.02rem;
}

.listener-favourites__section {
    margin-top: 1rem;
}

.listener-favourites__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.9rem 0.8rem;
    padding: 0 0.25rem;
}

.listener-favourites__album-card {
    position: relative;
}

.listener-favourites__album-main {
    width: 100%;
    border: 0;
    background: transparent;
    text-align: left;
}

.listener-favourites__album-image {
    width: 100%;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    border-radius: 1.3rem;
    box-shadow: 0 18px 32px rgba(148, 163, 184, 0.18);
}

.listener-favourites__album-title,
.listener-favourites__album-sub,
.listener-favourites__row-title,
.listener-favourites__row-sub {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-favourites__album-title {
    margin-top: 0.75rem;
    font-size: 0.95rem;
    font-weight: 700;
    color: #111827;
}

.listener-favourites__album-sub {
    margin-top: 0.25rem;
    font-size: 0.8rem;
    color: #64748b;
}

.listener-favourites__list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0 0.25rem;
}

.listener-favourites__row {
    border: 0;
    width: 100%;
    padding: 0.82rem;
    border-radius: 1.35rem;
    background: rgba(255, 255, 255, 0.78);
    display: flex;
    align-items: center;
    gap: 0.82rem;
    text-align: left;
    color: #111827;
    box-shadow: 0 14px 30px rgba(148, 163, 184, 0.12);
    backdrop-filter: blur(12px);
}

.listener-favourites__row.is-current {
    background: linear-gradient(135deg, rgba(255,255,255,0.95), rgba(226,232,240,0.86));
    box-shadow: 0 16px 34px rgba(148, 163, 184, 0.14);
}

.listener-favourites__row.is-current .listener-favourites__row-sub,
.listener-favourites__row.is-current .listener-favourites__duration,
.listener-favourites__row.is-current .listener-favourites__row-arrow {
    color: #64748b;
}

.listener-favourites__artist-image,
.listener-favourites__track-image {
    width: 3.4rem;
    height: 3.4rem;
    object-fit: cover;
    flex-shrink: 0;
}

.listener-favourites__artist-image {
    border-radius: 999px;
}

.listener-favourites__track-image {
    border-radius: 1rem;
}

.listener-favourites__row-meta {
    min-width: 0;
    flex: 1;
}

.listener-favourites__row-title {
    font-size: 0.95rem;
    font-weight: 700;
}

.listener-favourites__row-sub {
    margin-top: 0.22rem;
    font-size: 0.78rem;
    color: #64748b;
}

.listener-favourites__row-arrow {
    width: 1.05rem;
    height: 1.05rem;
    color: #94a3b8;
    flex-shrink: 0;
}

.listener-favourites__fav-button {
    width: 2.5rem;
    height: 2.5rem;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    color: #111827;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 12px 24px rgba(148, 163, 184, 0.14);
}

.listener-favourites__fav-button svg {
    width: 1.15rem;
    height: 1.15rem;
}

.listener-favourites__fav-button--inline {
    flex-shrink: 0;
}

.listener-favourites__album-card .listener-favourites__fav-button {
    position: absolute;
    top: 0.7rem;
    right: 0.7rem;
}

.listener-favourites__duration {
    font-size: 0.76rem;
    font-weight: 700;
    color: #94a3b8;
    flex-shrink: 0;
}

.listener-favourites__empty,
.listener-favourites__state {
    margin-top: 1.2rem;
    padding: 2rem 1.2rem;
    border-radius: 1.75rem;
    background: rgba(255, 255, 255, 0.74);
    text-align: center;
    color: #475569;
    box-shadow: 0 16px 34px rgba(148, 163, 184, 0.12);
}

.listener-favourites__empty h2,
.listener-favourites__state p {
    margin-top: 0.8rem;
}

.listener-favourites__empty h2 {
    font-size: 1rem;
    font-weight: 700;
    color: #111827;
}

.listener-favourites__empty p {
    margin-top: 0.4rem;
    font-size: 0.84rem;
    line-height: 1.6;
    color: #64748b;
}

.listener-favourites__empty-icon {
    width: 4rem;
    height: 4rem;
    margin: 0 auto;
    border-radius: 1.4rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.06);
    color: #111827;
}

.listener-favourites__empty-icon svg {
    width: 1.55rem;
    height: 1.55rem;
}

.listener-favourites__loading {
    width: 2.3rem;
    height: 2.3rem;
    margin: 0 auto;
    border-radius: 999px;
    border: 3px solid rgba(148, 163, 184, 0.2);
    border-top-color: #111827;
    animation: listener-favourites-spin 0.85s linear infinite;
}

@keyframes listener-favourites-spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
